import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import {
  LuPlane,
  LuTruck,
  LuUser,
  LuCreditCard,
  LuCheck,
} from 'react-icons/lu';
import { FaWhatsapp } from 'react-icons/fa';
import { Reveal, ScrollText } from '../../components/motion';
import { whatsappLink } from '../../constants/contact';
import Icon from '../../components/ui/Icon';
import { submitBooking } from '../../api/enquiries';
import {
  fetchCountries,
  fetchPaymentTypes,
  type MasterOption,
} from '../../api/masters';
import { useMasterDataLive } from '../../hooks/useMasterDataLive';
import PhoneCodeSelect from '../../components/ui/PhoneCodeSelect';
import SearchableSelect from '../../components/ui/SearchableSelect';
import TurnstileWidget, {
  TURNSTILE_SITE_KEY,
  type TurnstileHandle,
} from '../../components/ui/Turnstile';
import {
  LocationSearch,
  type LocationResult,
} from '../../components/ui/LocationSearch';
import {
  DEFAULT_DIAL_CODE,
  dialCodeForCountry,
  phoneProblem,
} from '../../data/dialCodes';
import usePageMeta from '../../hooks/usePageMeta';
import type { QuotePrefill } from '../Home/sections/FreeQuoteSection';
import styles from './BookShipment.module.css';

type ShipmentType = 'international' | 'domestic';
type Stage = 'select' | 'form' | 'success';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconComp = any;

const STEPS: Array<{ key: StepKey; label: string; icon: IconComp }> = [
  { key: 'shipper', label: 'Shipper', icon: LuUser },
  { key: 'receiver', label: 'Receiver', icon: LuUser },
  { key: 'shipment', label: 'Shipment', icon: LuTruck },
  { key: 'payment', label: 'Payment', icon: LuCreditCard },
];
type StepKey = 'shipper' | 'receiver' | 'shipment' | 'payment';

// Required fields per step — Continue is blocked until these are filled.
const REQUIRED: Record<StepKey, string[]> = {
  // Shipper: only name + phone are mandatory; email, country, pickup location
  // and address are optional (the submit payload already omits blanks).
  shipper: ['name', 'phone'],
  // The receiver's email is optional — only the shipper's is submitted. Country
  // is not shown on the receiver form, so only name + phone are required.
  receiver: ['name', 'phone'],
  shipment: ['content', 'estimatedWeight', 'numberOfPieces'],
  payment: [],
};

// Names the "… is required." message uses. Keyed by the field name, so the copy
// stays in step with REQUIRED rather than with each <Field label>.
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  email: 'Email',
  country: 'Country',
  phone: 'Phone number',
  location: 'Pickup location',
  content: 'Shipment content',
  estimatedWeight: 'Estimated weight',
  numberOfPieces: 'Number of pieces',
};

// `location` is the Google Places pick (→ enquiry `origin` / `destination`,
// plus its lat/lng/city columns) and `address` is the door-level detail the
// driver reads (→ `pickupAddress` / `receiverAddress`) — the same split the ops
// enquiry form uses.
const emptyParty = () => ({
  name: '',
  email: '',
  country: '',
  phoneCode: DEFAULT_DIAL_CODE,
  phone: '',
  location: null as LocationResult | null,
  address: '',
});

// Keyed to match the server's enquiry fields: `estimatedWeight` is kilograms
// (the unit the ops enquiry form assumes) and `numberOfPieces` is the count.
const emptyShipment = () => ({
  content: '',
  estimatedWeight: '',
  numberOfPieces: '1',
  pickupDate: '',
});

// `<input type="date">` and the server's DATEONLY column share the YYYY-MM-DD
// format, so the value needs no conversion on its way out.
const isoDay = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const todayISO = () => isoDay(0);
// "2026-10-18" → "18 Oct", read as a local date (no timezone shift).
const shortDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Phones get their own one-column layout of the same form (same fields, same
// rules) — the two-column grid squeezed every box until its text was cut off.
const PHONE_QUERY = '(max-width: 767.98px)';
const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
};

// One-tap answers for "What's inside?" on phones. "Other" opens the text box.
const CONTENT_PRESETS = ['Clothes', 'Food items', 'Documents', 'Electronics', 'Household'];

type Data = {
  shipper: ReturnType<typeof emptyParty>;
  receiver: ReturnType<typeof emptyParty>;
  shipment: ReturnType<typeof emptyShipment>;
  payment: string;
};

const BookShipment: React.FC = () => {
  usePageMeta({
    title: 'Book Cargo or Courier from Saudi Arabia | Sahari Cargo',
    description:
      'Book a cargo or courier shipment from Saudi Arabia online — domestic or international, by air or sea. Pickup from your address across KSA.',
    url: 'https://www.saharicargo.com/book-shipment',
  });
  // Arriving from the Home "Free quote" banner: name, mobile and destination
  // are already known, so open straight on the international form, prefilled.
  const quote = (useLocation().state as { quote?: QuotePrefill } | null)?.quote;
  const [selected, setSelected] = useState<ShipmentType>('international');
  const [stage, setStage] = useState<Stage>(quote ? 'form' : 'select');
  const [step, setStep] = useState(0);
  const [data, setData] = useState<Data>(() => {
    const shipper = emptyParty();
    const receiver = emptyParty();
    if (quote) {
      shipper.name = quote.name;
      shipper.phoneCode = quote.phoneCode;
      shipper.phone = quote.phone;
      if (quote.country) {
        receiver.country = quote.country;
        receiver.phoneCode = dialCodeForCountry(quote.country) ?? receiver.phoneCode;
      }
    }
    return { shipper, receiver, shipment: emptyShipment(), payment: 'pickup' };
  });
  const [errors, setErrors] = useState<Set<string>>(new Set());
  // Explanatory text for a failed field, keyed like `errors` (`section.field`).
  // Presence errors need no message — the red border says it.
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const isPhone = useIsPhone();

  // The Back / Continue bar sits at the bottom of the phone screen while the
  // form is open; lift the WhatsApp button above it so the two never overlap.
  useEffect(() => {
    if (!(isPhone && stage === 'form')) return;
    document.body.classList.add('waRaised');
    return () => document.body.classList.remove('waRaised');
  }, [isPhone, stage]);

  // Master data (managed in the admin web app's Master modules), loaded from
  // the public endpoints so every dropdown reflects the real DB.
  const [countries, setCountries] = useState<MasterOption[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<MasterOption[]>([]);

  useEffect(() => {
    fetchCountries().then(setCountries);
    fetchPaymentTypes().then(setPaymentTypes);
  }, []);

  // Live master-data: when the admin changes a master, refetch just the
  // affected dropdown instantly (no page refresh).
  useMasterDataLive((resource) => {
    if (resource === 'geography') {
      fetchCountries().then(setCountries);
    } else if (resource === 'payment-types') {
      fetchPaymentTypes().then(setPaymentTypes);
    }
  });

  // Default the payment choice to the first Master payment type once loaded.
  useEffect(() => {
    if (!paymentTypes.length) return;
    setData((d) =>
      paymentTypes.some((p) => p.name === d.payment)
        ? d
        : { ...d, payment: paymentTypes[0].name },
    );
  }, [paymentTypes]);

  const stepKey = STEPS[step].key;

  const setField = (
    section: StepKey,
    field: string,
    value: string | LocationResult | null,
  ) => {
    setData((d) => ({ ...d, [section]: { ...(d[section] as object), [field]: value } }));
    setErrors((prev) => {
      if (!prev.has(`${section}.${field}`)) return prev;
      const next = new Set(prev);
      next.delete(`${section}.${field}`);
      return next;
    });
    // Editing the dial code re-validates the number against a different rule,
    // so clear the phone message on either half of the field.
    const touched =
      field === 'phoneCode' ? `${section}.phone` : `${section}.${field}`;
    setMessages((prev) => {
      if (!(touched in prev)) return prev;
      const { [touched]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const options: Array<{ key: ShipmentType; label: string; icon: IconComp }> = [
    { key: 'international', label: 'International', icon: LuPlane },
    { key: 'domestic', label: 'Domestic', icon: LuTruck },
  ];

  const startBooking = (type: ShipmentType) => {
    setSelected(type);
    setStep(0);
    setErrors(new Set());
    setMessages({});
    setStage('form');
  };

  const goToStep = (i: number) => {
    setErrors(new Set());
    setMessages({});
    setStep(i);
  };

  const handleBack = () => {
    setErrors(new Set());
    setMessages({});
    if (step > 0) setStep((s) => s - 1);
    else setStage('select');
  };

  const handleContinue = async () => {
    if (submitting) return;
    const req = REQUIRED[stepKey];
    const section = data[stepKey] as Record<string, unknown>;
    // `location` holds a picked place object; every other field is a string.
    const missing = req.filter((f) => {
      const v = section?.[f];
      return typeof v === 'string' ? !v.trim() : !v;
    });
    if (missing.length) {
      setErrors(new Set(missing.map((f) => `${stepKey}.${f}`)));
      setMessages(
        Object.fromEntries(
          missing.map((f) => [
            `${stepKey}.${f}`,
            `${FIELD_LABELS[f] ?? f} is required.`,
          ]),
        ),
      );
      return;
    }
    // Everything is filled in — now check the phone against its dial code.
    if (stepKey === 'shipper' || stepKey === 'receiver') {
      const party = data[stepKey];
      const problem = phoneProblem(party.phoneCode, party.phone);
      if (problem) {
        setErrors(new Set([`${stepKey}.phone`]));
        setMessages({ [`${stepKey}.phone`]: problem });
        return;
      }
    }
    setErrors(new Set());
    setMessages({});
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    // Payment step → record the booking as a public enquiry in the DB.
    if (TURNSTILE_SITE_KEY && !captchaToken) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const { enquiryNumber } = await submitBooking({
        shipmentType: selected,
        shipper: data.shipper,
        receiver: data.receiver,
        shipment: data.shipment,
        payment: data.payment,
      }, captchaToken);
      setReference(enquiryNumber ? `#${enquiryNumber}` : '');
      setStage('success');
    } catch (e) {
      setSubmitError(
        e instanceof Error ? e.message : 'Could not submit your booking.',
      );
    } finally {
      setSubmitting(false);
      turnstileRef.current?.reset();
    }
  };

  const resetAll = () => {
    setData({
      shipper: emptyParty(),
      receiver: emptyParty(),
      shipment: emptyShipment(),
      payment: 'pickup',
    });
    setErrors(new Set());
    setStep(0);
    setStage('select');
  };

  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <section
      className={`${styles.page} ${stage === 'form' ? styles.pageForm : ''}`}
    >
      <Container fluid="xl" className={styles.inner}>
        {stage === 'select' && (
          <Reveal as="div" className={styles.card}>
            <ScrollText as="h1" className={styles.title} mode="enter">
              Book a Shipment
            </ScrollText>
            <p className={styles.subtitle}>
              Book cargo or courier from Saudi Arabia in a few simple steps
            </p>
            <p className={styles.question}>International or Domestic ?</p>
            <div className={styles.options} role="radiogroup" aria-label="Shipment type">
              {options.map(({ key, label, icon }) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.option} ${selected === key ? styles.optionActive : ''}`}
                  onClick={() => startBooking(key)}
                >
                  <Icon icon={icon} className={styles.optionIcon} />
                  <span className={styles.optionLabel}>{label}</span>
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {stage === 'form' && (
          <div className={styles.formWrap}>
            {isPhone ? <PhoneStepper current={step} /> : <Stepper current={step} />}
            <Reveal key={step} as="div" className={styles.formCard} distance={14}>
              {(stepKey === 'shipper' || stepKey === 'receiver') && (
                <PartyForm
                  title={`Please fill the ${STEPS[step].label} details`}
                  isPhone={isPhone}
                  section={stepKey}
                  values={data[stepKey]}
                  errors={errors}
                  messages={messages}
                  onField={setField}
                  onSubmit={handleContinue}
                  countries={countries}
                />
              )}
              {stepKey === 'shipment' && (
                <ShipmentForm
                  isPhone={isPhone}
                  values={data.shipment}
                  errors={errors}
                  messages={messages}
                  onField={setField}
                  onSubmit={handleContinue}
                />
              )}
              {stepKey === 'payment' && (
                <PaymentForm
                  value={data.payment}
                  onChange={(v) => setData((d) => ({ ...d, payment: v }))}
                  paymentTypes={paymentTypes}
                  review={isPhone ? data : undefined}
                  onEdit={goToStep}
                />
              )}
              {submitError && <p className={styles.submitError}>{submitError}</p>}
              {isLast && (
                <TurnstileWidget
                  ref={turnstileRef}
                  onToken={setCaptchaToken}
                  className={styles.captcha}
                />
              )}
              <FormActions
                backLabel={isFirst ? 'Cancel' : 'Back'}
                continueLabel={
                  isLast
                    ? submitting
                      ? 'Submitting…'
                      : isPhone
                        ? 'Submit booking'
                        : 'Submit'
                    : 'Continue'
                }
                onBack={handleBack}
                onContinue={handleContinue}
                busy={submitting}
                continueDisabled={
                  isLast && !!TURNSTILE_SITE_KEY && !captchaToken
                }
              />
            </Reveal>
          </div>
        )}

        {stage === 'success' && (
          <Reveal as="div" className={`${styles.card} ${styles.successCard}`}>
            <span className={styles.successBadge}>
              <Icon icon={LuCheck} className={styles.successCheck} />
            </span>
            <h1 className={styles.successTitle}>
              {isPhone ? 'Booking received' : 'Booking Successfully Submitted'}
            </h1>
            <p className={styles.successNote}>
              Our team will contact you shortly to confirm pickup details and
              final pricing
            </p>
            {isPhone ? (
              <div className={styles.refBox}>
                <span>Your booking number</span>
                <b>{reference}</b>
              </div>
            ) : (
              <p className={styles.successRef}>Booking reference : {reference}</p>
            )}
            {isPhone && (
              <a
                href={whatsappLink(
                  `Hi Sahari Cargo, I just booked a shipment${reference ? ` (${reference})` : ''}.`,
                )}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.waBtn}
              >
                <Icon icon={FaWhatsapp} aria-hidden="true" /> Chat on WhatsApp
              </a>
            )}
            <button type="button" className={styles.bookMore} onClick={resetAll}>
              {isPhone ? 'Book another shipment' : 'Book More Shipment'}
            </button>
          </Reveal>
        )}
      </Container>
    </section>
  );
};

/* ── Stepper ─────────────────────────────────────────────────────────── */

const Stepper: React.FC<{ current: number }> = ({ current }) => (
  <div className={styles.stepper}>
    {STEPS.map((s, i) => {
      const state = i === current ? styles.stepActive : i < current ? styles.stepDone : '';
      return (
        <React.Fragment key={s.key}>
          <div className={`${styles.step} ${state}`}>
            <span className={styles.stepIcon}>
              <Icon icon={s.icon} />
            </span>
            <span className={styles.stepLabel}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <span
              className={`${styles.stepLine} ${i <= current ? styles.stepLineDone : ''}`}
              aria-hidden="true"
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* Phones: "Step 1 of 4" with four bars — the icon row needed the whole width. */
const PhoneStepper: React.FC<{ current: number }> = ({ current }) => (
  <div className={styles.phoneStepper}>
    <span className={styles.phoneStepCount}>
      Step {current + 1} of {STEPS.length}
    </span>
    <div className={styles.phoneBars} aria-hidden="true">
      {STEPS.map((s, i) => (
        <i key={s.key} className={i <= current ? styles.phoneBarDone : ''} />
      ))}
    </div>
    <div className={styles.phoneBarLabels}>
      {STEPS.map((s, i) => (
        <span
          key={s.key}
          className={i === current ? styles.phoneLabelOn : i < current ? styles.phoneLabelDone : ''}
        >
          {s.label}
        </span>
      ))}
    </div>
  </div>
);

/* ── Shared field pieces ─────────────────────────────────────────────── */

const Field: React.FC<{
  label: string;
  full?: boolean;
  required?: boolean;
  /** Phones say "Optional" beside the label of every field that can be left blank. */
  optional?: boolean;
  /** Validation text shown under the control. The red border alone is silent. */
  error?: string;
  children: React.ReactNode;
}> = ({ label, full, required, optional, error, children }) => (
  <label className={`${styles.field} ${full ? styles.full : ''}`}>
    <span className={styles.fieldLabel}>
      {label}
      {required && (
        <span className={styles.req} aria-hidden="true">
          {' '}
          *
        </span>
      )}
      {optional && <span className={styles.optional}>Optional</span>}
    </span>
    {children}
    {error && (
      <span className={styles.fieldError} role="alert">
        {error}
      </span>
    )}
  </label>
);

const cx = (base: string, err: boolean) =>
  `${base} ${err ? styles.inputError : ''}`;

/* ── Party form (Shipper / Receiver) ─────────────────────────────────── */

const PartyForm: React.FC<{
  title: string;
  section: StepKey;
  values: ReturnType<typeof emptyParty>;
  errors: Set<string>;
  messages: Record<string, string>;
  onField: (section: StepKey, field: string, value: string | LocationResult | null) => void;
  onSubmit: () => void;
  countries: MasterOption[];
  isPhone: boolean;
}> = ({ title, section, values, errors, messages, onField, onSubmit, countries, isPhone }) => {
  const isShipper = section === 'shipper';
  const err = (f: string) => errors.has(`${section}.${f}`);
  const msg = (f: string) => messages[`${section}.${f}`];
  const on =
    (f: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onField(section, f, e.target.value);

  // Picking a country pre-selects its dial code. A country with no entry in the
  // bundled list leaves whatever the user already chose in place.
  const onCountry = (country: string) => {
    onField(section, 'country', country);
    const code = dialCodeForCountry(country);
    if (code) onField(section, 'phoneCode', code);
  };

  const nameField = (
    <Field label={isPhone ? 'Full name' : 'Name'} required error={msg('name')}>
      <input className={cx(styles.input, err('name'))} placeholder={isPhone ? (isShipper ? 'e.g. Mohammed Ali' : 'e.g. Fathima K') : 'Evil Rabbit'} value={values.name} onChange={on('name')} autoComplete="name" />
    </Field>
  );
  const emailField = (
    <Field label="Email" optional={isPhone} error={msg('email')}>
      <input type="email" className={cx(styles.input, err('email'))} placeholder={isPhone ? 'name@email.com' : 'example@acme.com'} value={values.email} onChange={on('email')} />
    </Field>
  );
  // Country is shown on the Shipper form only (it also pre-fills the dial
  // code). The Receiver form omits it.
  const countryField = isShipper && (
    <Field label="Country" optional={isPhone} error={msg('country')}>
      <SearchableSelect
        value={values.country}
        onChange={onCountry}
        options={countries}
        placeholder="Select country"
        emptyText="No matching country"
        invalid={err('country')}
        aria-label="Country"
      />
    </Field>
  );
  const phoneField = (
    <Field label="Phone Number" required error={msg('phone')}>
      <div className={styles.phoneRow}>
        <PhoneCodeSelect
          value={values.phoneCode}
          onChange={(code) => onField(section, 'phoneCode', code)}
          invalid={err('phone')}
        />
        <input
          type="tel"
          inputMode="tel"
          className={cx(styles.input, err('phone'))}
          placeholder="50 123 4567"
          value={values.phone}
          onChange={on('phone')}
          aria-invalid={err('phone')}
        />
      </div>
    </Field>
  );
  const locationField = (
    <Field
      label={isShipper ? 'Pickup Location' : 'Delivery Location'}
      optional={isPhone}
      error={msg('location')}
    >
      <LocationSearch
        value={values.location}
        onChange={(loc) => onField(section, 'location', loc)}
        invalid={err('location')}
        inputClassName={cx(styles.input, err('location'))}
        aria-label={isShipper ? 'Pickup location' : 'Delivery location'}
      />
    </Field>
  );
  const addressField = (
    <Field
      label={isShipper ? 'Pickup Address' : 'Receiver Address'}
      optional={isPhone}
      error={msg('address')}
    >
      <textarea
        className={cx(styles.textarea, err('address'))}
        rows={2}
        placeholder="Door number, building, floor..."
        value={values.address}
        onChange={on('address')}
      />
    </Field>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h2 className={styles.formTitle}>
        {isPhone ? (isShipper ? 'Who is sending the parcel?' : 'Who will receive it?') : title}
      </h2>
      <p className={styles.formNote}>
        {isPhone
          ? isShipper
            ? 'We call this number to confirm the pickup.'
            : 'We share this with our delivery team at the destination.'
          : 'You are currently on the free plan. Upgrade to the pro plan to get access to all features.'}
      </p>

      {/* Phones put the two required answers first, then the optional rest. */}
      <div className={styles.grid}>
        {isPhone ? (
          <>
            {nameField}
            {phoneField}
            <div className={styles.moreDivider}>More details</div>
            {emailField}
            {countryField}
          </>
        ) : (
          <>
            {nameField}
            {emailField}
            {countryField}
            {phoneField}
          </>
        )}
        {locationField}
        {addressField}
      </div>
    </form>
  );
};

/* ── Shipment form ───────────────────────────────────────────────────── */

const ShipmentForm: React.FC<{
  values: ReturnType<typeof emptyShipment>;
  errors: Set<string>;
  messages: Record<string, string>;
  onField: (section: StepKey, field: string, value: string) => void;
  onSubmit: () => void;
  isPhone: boolean;
}> = ({ values, errors, messages, onField, onSubmit, isPhone }) => {
  const err = (f: string) => errors.has(`shipment.${f}`);
  const msg = (f: string) => messages[`shipment.${f}`];
  const on =
    (f: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onField('shipment', f, e.target.value);
  // Typed something that is not one of the chips (or tapped "Other") → keep
  // the text box open so it can be read and changed.
  const [otherOpen, setOtherOpen] = useState(
    () => !!values.content && !CONTENT_PRESETS.includes(values.content),
  );
  const [chooseDate, setChooseDate] = useState(
    () => !!values.pickupDate && values.pickupDate !== isoDay(0) && values.pickupDate !== isoDay(1),
  );
  // − / + on the weight and piece boxes. Never below 1.
  const bump = (f: 'estimatedWeight' | 'numberOfPieces', by: number) => {
    const cur = Number(values[f]) || 0;
    onField('shipment', f, String(Math.max(1, Math.round((cur + by) * 10) / 10)));
  };

  if (isPhone) {
    const dayChip = chooseDate
      ? 'choose'
      : values.pickupDate === isoDay(0)
        ? 'today'
        : values.pickupDate === isoDay(1)
          ? 'tomorrow'
          : '';
    const pickDay = (key: 'today' | 'tomorrow' | 'choose') => {
      if (key === dayChip) {
        // Tapping the chosen day again clears it — the pickup day is optional.
        setChooseDate(false);
        onField('shipment', 'pickupDate', '');
        return;
      }
      setChooseDate(key === 'choose');
      onField('shipment', 'pickupDate', key === 'choose' ? '' : isoDay(key === 'today' ? 0 : 1));
    };
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <h2 className={styles.formTitle}>What are you sending?</h2>
        <p className={styles.formNote}>A rough idea is enough. We weigh it at pickup.</p>

        <div className={styles.grid}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              What&apos;s inside?<span className={styles.req} aria-hidden="true"> *</span>
            </span>
            <div className={styles.chips} role="group" aria-label="What's inside">
              {CONTENT_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={!otherOpen && values.content === c}
                  className={`${styles.chip} ${!otherOpen && values.content === c ? styles.chipOn : ''}`}
                  onClick={() => {
                    setOtherOpen(false);
                    onField('shipment', 'content', c);
                  }}
                >
                  {c}
                </button>
              ))}
              <button
                type="button"
                aria-pressed={otherOpen}
                className={`${styles.chip} ${otherOpen ? styles.chipOn : ''}`}
                onClick={() => {
                  setOtherOpen(true);
                  onField('shipment', 'content', '');
                }}
              >
                Other
              </button>
            </div>
            {otherOpen && (
              <input
                className={cx(styles.input, err('content'))}
                placeholder="Type what is inside"
                value={values.content}
                onChange={on('content')}
                autoFocus
              />
            )}
            {msg('content') && (
              <span className={styles.fieldError} role="alert">
                {msg('content')}
              </span>
            )}
          </div>

          <div className={styles.twoCol}>
            {/* A <div>, not <Field>'s <label>: tapping the label text would press "−". */}
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                Weight<span className={styles.req} aria-hidden="true"> *</span>
              </span>
              <div className={cx(styles.counter, err('estimatedWeight'))}>
                <button type="button" aria-label="Less weight" onClick={() => bump('estimatedWeight', -1)}>−</button>
                <input type="number" inputMode="decimal" min={0} step={0.1} placeholder="0" value={values.estimatedWeight} onChange={on('estimatedWeight')} aria-label="Estimated weight in kg" />
                <button type="button" aria-label="More weight" onClick={() => bump('estimatedWeight', 1)}>+</button>
              </div>
              <span className={styles.unit}>kg (approx.)</span>
              {msg('estimatedWeight') && (
                <span className={styles.fieldError} role="alert">{msg('estimatedWeight')}</span>
              )}
            </div>
            {/* A <div>, not <Field>'s <label>: tapping the label text would press "−". */}
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                Pieces<span className={styles.req} aria-hidden="true"> *</span>
              </span>
              <div className={cx(styles.counter, err('numberOfPieces'))}>
                <button type="button" aria-label="Fewer pieces" onClick={() => bump('numberOfPieces', -1)}>−</button>
                <input type="number" inputMode="numeric" min={1} placeholder="1" value={values.numberOfPieces} onChange={on('numberOfPieces')} aria-label="Number of pieces" />
                <button type="button" aria-label="More pieces" onClick={() => bump('numberOfPieces', 1)}>+</button>
              </div>
              <span className={styles.unit}>boxes / bags</span>
              {msg('numberOfPieces') && (
                <span className={styles.fieldError} role="alert">{msg('numberOfPieces')}</span>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Pickup day<span className={styles.optional}>Optional</span>
            </span>
            <div className={styles.chips} role="group" aria-label="Pickup day">
              {(
                [
                  ['today', 'Today'],
                  ['tomorrow', 'Tomorrow'],
                  ['choose', 'Choose date'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={dayChip === key}
                  className={`${styles.chip} ${dayChip === key ? styles.chipOn : ''}`}
                  onClick={() => pickDay(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            {chooseDate && (
              <input
                type="date"
                min={todayISO()}
                className={cx(styles.input, err('pickupDate'))}
                value={values.pickupDate}
                onChange={on('pickupDate')}
                aria-label="Pickup date"
              />
            )}
          </div>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h2 className={styles.formTitle}>Please fill the Shipment details</h2>
      <p className={styles.formNote}>
        You are currently on the free plan. Upgrade to the pro plan to get access
        to all features.
      </p>

      <div className={styles.grid}>
        <Field label="Shipment Content" required error={msg('content')}>
          <input className={cx(styles.input, err('content'))} placeholder="Enter Shipment Content" value={values.content} onChange={on('content')} />
        </Field>
        <Field label="Estimated Weight (kg)" required error={msg('estimatedWeight')}>
          <input type="number" min={0} step={0.1} className={cx(styles.input, err('estimatedWeight'))} placeholder="Enter estimated weight" value={values.estimatedWeight} onChange={on('estimatedWeight')} />
        </Field>
        <Field label="Number of Pieces" required error={msg('numberOfPieces')}>
          <input type="number" min={1} className={cx(styles.input, err('numberOfPieces'))} placeholder="1" value={values.numberOfPieces} onChange={on('numberOfPieces')} />
        </Field>
        <Field label="Pickup Date" error={msg('pickupDate')}>
          <input type="date" min={todayISO()} className={cx(styles.input, err('pickupDate'))} value={values.pickupDate} onChange={on('pickupDate')} />
        </Field>
      </div>
    </form>
  );
};

/* ── Payment form ────────────────────────────────────────────────────── */

const PaymentForm: React.FC<{
  value: string;
  onChange: (v: string) => void;
  paymentTypes: MasterOption[];
  /** Phones only: everything typed so far, shown back once before Submit. */
  review?: Data;
  onEdit: (step: number) => void;
}> = ({ value, onChange, paymentTypes, review, onEdit }) => (
  <div>
    <h2 className={styles.formTitle}>{review ? 'How will you pay?' : 'Payment Method'}</h2>
    <p className={styles.formNote}>
      {review
        ? 'Final price is confirmed after we check the parcel.'
        : 'How would you like to pay?'}
    </p>

    <div className={styles.payList} role="radiogroup" aria-label="Payment method">
      {paymentTypes.map((p) => {
        const active = value === p.name;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={active}
            className={`${styles.payOption} ${active ? styles.payOptionActive : ''}`}
            onClick={() => onChange(p.name)}
          >
            <span className={`${styles.radio} ${active ? styles.radioActive : ''}`} aria-hidden="true" />
            <span className={styles.payText}>
              <span className={styles.payTitle}>{p.name}</span>
            </span>
          </button>
        );
      })}
    </div>

    {review ? (
      <>
        <div className={styles.moreDivider}>Check your details</div>
        <div className={styles.review}>
          {[
            {
              label: 'Shipper',
              text: `${review.shipper.name} · ${review.shipper.phoneCode} ${review.shipper.phone}`,
              step: 0,
            },
            {
              label: 'Receiver',
              text: `${review.receiver.name} · ${review.receiver.phoneCode} ${review.receiver.phone}`,
              step: 1,
            },
            {
              label: 'Shipment',
              text: [
                review.shipment.content,
                `${review.shipment.estimatedWeight} kg`,
                `${review.shipment.numberOfPieces} piece${review.shipment.numberOfPieces === '1' ? '' : 's'}`,
                review.shipment.pickupDate && `Pickup ${shortDay(review.shipment.pickupDate)}`,
              ]
                .filter(Boolean)
                .join(' · '),
              step: 2,
            },
          ].map((r) => (
            <div key={r.label} className={styles.reviewRow}>
              <div>
                <span>{r.label}</span>
                <p>{r.text}</p>
              </div>
              <button type="button" onClick={() => onEdit(r.step)}>
                Edit
              </button>
            </div>
          ))}
        </div>
      </>
    ) : (
      <p className={styles.payFootnote}>
        Final Pricing will be confirmed by our team, after verifying the shipment
        details. You will receive a message
      </p>
    )}
  </div>
);

/* ── Actions ─────────────────────────────────────────────────────────── */

const FormActions: React.FC<{
  backLabel: string;
  continueLabel: string;
  onBack: () => void;
  onContinue: () => void;
  busy?: boolean;
  continueDisabled?: boolean;
}> = ({ backLabel, continueLabel, onBack, onContinue, busy, continueDisabled }) => (
  <div className={styles.actionsRow}>
    <button
      type="button"
      className={styles.btnGhost}
      onClick={onBack}
      disabled={busy}
    >
      {backLabel}
    </button>
    <button
      type="button"
      className={styles.btnPrimary}
      onClick={onContinue}
      disabled={busy || continueDisabled}
    >
      {continueLabel}
    </button>
  </div>
);

export default BookShipment;
